import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { CreditProcessStatus, CreditStatus } from 'src/app/@core/constants';
import { ColumnTable } from 'src/app/models/column-table';
import { CreditRequest } from 'src/app/models/credit-request';
import { Filter } from 'src/app/models/filter';
import { GroupedFilterTable } from 'src/app/models/grouped-filter';
import { ResponseModel } from 'src/app/models/response-model';
import { OnbaseProcedureResponseModel } from 'src/app/models/response/onbaseProcedureResponseModel';
import { SalesOrg } from 'src/app/models/sales-org';
import { OnbaseService } from 'src/app/services/onbase.service';
import { SalesOrgService } from 'src/app/services/sales-org.service';
import { Branch } from './../../../../models/branch';
import { FilterTable } from './../../../../models/filter-table';
import { OnbaseRequestFilter } from './../../../../models/filters/onbase-request-filter';
import { Paging } from './../../../../models/page';
import { BranchesService } from './../../../../services/branches.service';
import { ConfigurationService } from './../../../../services/configuration.service';

@Component({
  selector: 'app-credit-request-list',
  templateUrl: './credit-request-list.component.html',
  styleUrls: ['./credit-request-list.component.sass'],
})
export class CreditRequestListComponent implements OnInit {
  creditRequests: Paging<Array<CreditRequest>> = new Paging([]);
  onbaseProcedures: Array<OnbaseProcedureResponseModel> = new Array();
  salesOrgs: Array<SalesOrg> = new Array();
  branches: Array<Branch> = new Array();
  badgeSuccessValues: Array<string> = new Array();
  badgeWarningValues: Array<string> = new Array();
  badgeSecondaryValues: Array<string> = new Array();
  badgeDangerValues: Array<string> = new Array();
  defaultFilters: Partial<OnbaseRequestFilter> = {};
  selectedSalesOrgFilter: string;

  constructor(
    private readonly onbaseService: OnbaseService,
    private readonly configService: ConfigurationService,
    private readonly datePipe: DatePipe,
    private readonly router: Router,
    private readonly salesOrgService: SalesOrgService,
    private readonly branchService: BranchesService
  ) {
    this.badgeSuccessValues = this.getBadgeSuccessValues();
    this.badgeWarningValues = this.getBadgeWarningValues();
    this.badgeSecondaryValues = this.getBadgeSecondaryValues();
    this.badgeDangerValues = this.getBadgeDangerValues();

    const { branch, salesOrg } =
      this.configService.readSelectedBranchAndSalesOrg();

    this.defaultFilters = {
      orderBy: "WITH_REQUIREMENTS",
      ascending: 'false',
      branchCode: branch.code,
      salesOrg: salesOrg.code,
    };

    this.selectedSalesOrgFilter = this.defaultFilters.salesOrg || '';
  }

  async ngOnInit(): Promise<void> {
    await this.loadFiltersData();

    await this.getData({
      page: 0,
      pageSize: 20,
      value: {},
    });
  }

  private async loadFiltersData() {
    this.onbaseProcedures = (
      await this.onbaseService.readCreditPlans().toPromise()
    ).response.content;

    this.salesOrgs = (
      await this.salesOrgService.readSalesOrgs().toPromise()
    ).response.content;

    this.branches = (
      await this.branchService.readBranches().toPromise()
    ).response.content;
  }

  getGroupedFilters(selectedSalesOrg: string) {
    return [
      new GroupedFilterTable("customers", "Clientes", [
        new FilterTable('requestCode', 'Código de Solicitud'),
        new FilterTable('customerCode', 'Código del Cliente'),
        new FilterTable('customerName', 'Nombre del Cliente'),
        new FilterTable('customerFiscalId', 'Identificación'),
      ]),
      new GroupedFilterTable("seller", "Vendedor", [
        new FilterTable('sellerUsername', 'Código del Vendedor'),
        new FilterTable('sellerName', 'Nombre del Vendedor'),
      ]),
      new GroupedFilterTable("creditPlans", "Planes de Credito", [
        new FilterTable('procedure', 'Plan de Credito', {
          type: 'combobox',
          comboBoxFilterValues: this.getOnbaseProcedureMap(),
        }),
        new FilterTable('requestStatus', 'Estado de OnBase', {
          type: 'combobox',
          comboBoxFilterValues: this.getOnbaseStatusMapValue(),
        }),
        new FilterTable('processingStatus', 'Estado del Proceso', {
          type: 'combobox',
          comboBoxFilterValues: this.getProcessingStatusMapValue(),
        }),
      ]),
      new GroupedFilterTable("creationDate", "Fecha de Solicitud",
        [
          new FilterTable('createdInitialDate', 'Creado Desde', { type: 'date' }),
          new FilterTable('createdEndDate', 'Creado Hasta', { type: 'date' }),
        ]),
      new GroupedFilterTable("finalDates", "Fecha Finalización", [
        new FilterTable('closedInitialDate', 'Finalizado Desde', {
          type: 'date',
        }),
        new FilterTable('closedEndDate', 'Finalizado Hasta', { type: 'date' }),
      ]),
      new GroupedFilterTable("salesOrg", "División o Empresa", [
        new FilterTable('salesOrg', 'Empresa', {
          type: 'combobox',
          comboBoxFilterValues: this.getSalesOrgMap(),
          hasDependecyComboBox: true,
        }),
        new FilterTable('branchCode', 'División', {
          type: 'combobox',
          comboBoxFilterValues: this.getBranchesMap(selectedSalesOrg),
        }),
      ]),
      new GroupedFilterTable("grouping", "Ordenar Por", [
        new FilterTable('orderBy', 'Agrupar Por', {
          type: 'combobox',
          comboBoxFilterValues: this.getOrderByMap(),
        }),
        new FilterTable('ascending', 'Ordenar', {
          type: 'combobox',
          comboBoxFilterValues: new Map<string, string>()
            .set('true', 'Ascendente')
            .set('false', 'Descendente'),
        }),
      ])

    ]
  }
  private getOriginCreditRequestMapValue(): Map<string, string> | undefined {
    return new Map<string, string>().set('WEB', 'WEB').set('STORE', 'TIENDA');
  }

  getHeaders(): Array<ColumnTable> {
    return [
      new ColumnTable('code', 'Código'),
      new ColumnTable('customer.mainPersonalInfo.fiscalId', 'No. Identidad'),
      new ColumnTable('customer.fullName', 'Cliente'),
      new ColumnTable('procedure', 'Plan de Crédito'),
      new ColumnTable('createdDate', 'Fecha de Solicitud'),
      new ColumnTable('createdHour', 'Hora de Solicitud'),
      new ColumnTable('parsedStatus', 'Estado Onbase', {
        type: 'badge',
        valueMap: this.getOnbaseStatusMapValue(),
      }),
      new ColumnTable('processingStatus', 'Estado del Proceso', {
        type: 'badge',
        valueMap: this.getProcessingStatusMapValue(),
      }),
      new ColumnTable('isAval', 'Tipo', {
        type: 'badge',
        valueMap: new Map().set('false', 'Cliente').set('true', 'Aval'),
      }),
      new ColumnTable('seller.fullname', 'Vendedor'),
      new ColumnTable('', 'Opciones', {
        type: 'action',
        iconClass: 'fa fa-cogs',
        isIcon: true,
        actions: [
          {
            fn: (creditRequest: CreditRequest) => {
              this.router.navigate([`/creditRequests`, creditRequest.code], {
                state: creditRequest,
              });
            },
            disabledFn: (creditRequest: CreditRequest) =>
              creditRequest.branchCode !=
              this.configService.readSelectedBranchAndSalesOrg().branch.code,
            icon: 'fa fa-regular fa-circle-right',
            tooltip: 'Ver Solicitud',
            isDisabled: false,
          },
          {
            fn: (creditRequest: CreditRequest) => {
              this.router.navigate(
                ['/creditRequests/modify', creditRequest.code],
                {
                  state: creditRequest,
                }
              );
            },
            disabledFn: (creditRequest: CreditRequest) =>
              creditRequest.branchCode !=
              this.configService.readSelectedBranchAndSalesOrg().branch
                .code ||
              creditRequest.processingStatus !== CreditProcessStatus.DRAFT,
            icon: 'fa fa-pen-to-square',
            tooltip: 'Completar Solicitud',
            isDisabled: false,
          },
        ],
      }),
    ];
  }

  private validateFilter(value: string | undefined) {
    return value !== '' && value !== undefined;
  }

  async getData(filters: Filter<OnbaseRequestFilter>): Promise<void> {
    const mergedFilters: Filter<OnbaseRequestFilter> = {
      page: filters.page,
      pageSize: filters.pageSize,
      value: {
        ...filters.value,
        orderBy: this.validateFilter(filters.value.orderBy)
          ? filters.value.orderBy
          : this.defaultFilters.orderBy,
        ascending: this.validateFilter(filters.value.ascending)
          ? filters.value.ascending
          : this.defaultFilters.ascending,
        branchCode: this.validateFilter(filters.value.branchCode)
          ? filters.value.branchCode
          : this.defaultFilters.branchCode,
        salesOrg: this.validateFilter(filters.value.salesOrg)
          ? filters.value.salesOrg
          : this.defaultFilters.salesOrg,
      },
    };

    if (mergedFilters.value.salesOrg !== this.defaultFilters.salesOrg) {
      mergedFilters.value.branchCode = filters.value.branchCode;
    }

    this.creditRequests = (
      await this.onbaseService
        .readOnbaseRequestByFilters(mergedFilters)
        .pipe(
          map((serviceResponse) =>
            this.transformCreditRequestData(serviceResponse)
          )
        )
        .toPromise()
    ).response;
  }

  private transformCreditRequestData(
    serviceResponse: ResponseModel<Paging<CreditRequest[]>>
  ) {
    serviceResponse.response.content.forEach((creditRequest) => {
      creditRequest.procedure = this.getProcedureWithName(creditRequest);

      creditRequest.createdDate = this.transformCreatedDate(creditRequest);

      creditRequest.createdHour = this.transformCreatedHour(creditRequest);

      creditRequest.parsedStatus = this.getStatus(creditRequest);

      creditRequest.isAval = `${this.getIsAvalRequest(creditRequest)}`;

      creditRequest.hasRequirements = creditRequest.status.includes(CreditStatus.WITH_REQUIREMENTS) ? false : undefined;
    });
    return serviceResponse;
  }

  private getIsAvalRequest(creditRequest: CreditRequest): boolean {
    return creditRequest.requestData.Codigo_Solicitud_Principal != undefined && creditRequest.requestData.Codigo_Solicitud_Principal != '';
  }

  private getProcedureWithName(creditRequest: CreditRequest): string {
    return `${this.onbaseProcedures.find((o) => o.code === creditRequest.procedure)
      ?.name
      } (${creditRequest.procedure})`;
  }

  private getStatus(creditRequest: CreditRequest): string {
    return creditRequest.status[0] ? creditRequest.status[0] : ``;
  }

  private transformCreatedHour(creditRequest: CreditRequest): string {
    return (
      this.datePipe.transform(
        creditRequest.createdTimezoneDate,
        `hh:mm:ss a`
      ) || ``
    );
  }

  private transformCreatedDate(creditRequest: CreditRequest): string {
    return (
      this.datePipe.transform(
        creditRequest.createdTimezoneDate,
        `dd-MM-yyyy`
      ) || ``
    );
  }

  getOnbaseStatusMapValue(): Map<string, string> {
    return new Map<string, string>()
      .set('NO_STATUS', 'SIN ESTADO')
      .set('APPROVED', 'APROBADA')
      .set('REJECTED', 'RECHAZADA')
      .set('CONTROL_TABLE', 'MESA DE CONTROL')
      .set('TELEPHONE_INVESTIGATION', 'INVESTIGACIÓN TELEFÓNICA')
      .set('HOME_DOMESTIC_INVESTIGATION', 'INVESTIGACIÓN DOMICILIARIA CASA')
      .set('WORK_DOMESTIC_INVESTIGATION', 'INVESTIGACIÓN DOMICILIARIA TRABAJO')
      .set('HUMAN_RESOURCES', 'RECURSOS HUMANOS')
      .set('ANALISYS_AND_APPROVAL', 'ANÁLISIS Y APROBACIÓN')
      .set('REPROGRAMMING_IT', 'REPROGRAMACIÓN IT')
      .set('REPROGRAMMING_HOUSE_ID', 'REPROGRAMACIÓN CASA')
      .set('REPROGRAMMING_WORK_ID', 'REPROGRAMACIÓN TRABAJO')
      .set('WITH_REQUIREMENTS', 'EN REQUERIMIENTO')
      .set('', '');
  }
  getProcessingStatusMapValue(): Map<string, string> {
    return new Map<string, string>()
      .set('PROCESSED', 'PROCESADA')
      .set('FAILED', 'FALLO')
      .set('UNPROCESSED', 'PROCESANDO...')
      .set('DRAFT', 'EN BORRADORES');
  }

  getBadgeSuccessValues(): Array<string> {
    return ['APPROVED', 'PROCESSED'];
  }

  getBadgeWarningValues(): Array<string> {
    return [
      'TELEPHONE_INVESTIGATION',
      'CONTROL_TABLE',
      'HOME_DOMESTIC_INVESTIGATION',
      'WORK_DOMESTIC_INVESTIGATION',
      'HUMAN_RESOURCES',
      'ANALISYS_AND_APPROVAL',
      'WITH_REQUIREMENTS',
      'DRAFT',
    ];
  }

  getBadgeSecondaryValues(): Array<string> {
    return [
      'NO_STATUS',
      'REPROGRAMMING_IT',
      'REPROGRAMMING_HOUSE_ID',
      'REPROGRAMMING_WORK_ID',
      'UNPROCESSED',
      '',
      'true',
      'false'
    ];
  }

  getBadgeDangerValues(): Array<string> {
    return ['REJECTED', 'FAILED'];
  }

  getOnbaseProcedureMap(): Map<string, string> | undefined {
    let result = new Map<string, string>();
    this.onbaseProcedures.forEach((op) =>
      result.set(`${op.code}`, `${op.name}`)
    );
    return result;
  }

  getSalesOrgMap(): Map<string, string> | undefined {
    const result = new Map<string, string>();
    this.salesOrgs.forEach((salesOrg) =>
      result.set(salesOrg.code, salesOrg.name)
    );
    return result;
  }

  getBranchesMap(selectedSalesOrg: string): Map<string, string> | undefined {
    const result = new Map<string, string>();
    this.branches
      .filter((branch) => branch.salesOrg === selectedSalesOrg)
      .forEach((branch) => result.set(branch.code, branch.name));
    return result;
  }

  updateBranchesFilterDatasource(data: {
    filter: FilterTable;
    selectedValue: string;
  }) {
    this.selectedSalesOrgFilter =
      data.selectedValue !== '' && data.selectedValue !== undefined
        ? data.selectedValue
        : this.defaultFilters.salesOrg || '';
  }

  getOrderByMap(): Map<string, string> {
    return new Map<string, string>()
      .set('SELLER', 'Vendedor')
      .set('CLOSED', 'Fecha Finalizada')
      .set('REQUESTED', 'Fecha de Solicitud')
      .set('WITH_REQUIREMENTS', 'En Requerimiento');
  }
}
